/**
 * Shared field mapping configs and helpers for marketplace listing widgets.
 * Used by all Marketplace*Widget components to handle TikTok vs Lazada field differences.
 */

/** Field mapping config — maps generic keys to DocType-specific fieldnames */
export interface ChannelFieldConfig {
  title: string;
  sku: string;
  skuId: string;
  images: string;
  category: string;
  brandName: string;
  price: string;
  currency: string | null;
  weight: string;
  dimensions: string | null; // TikTok: JSON object; Lazada: null (separate fields)
  dimensionWidth: string | null;
  dimensionHeight: string | null;
  dimensionLength: string | null;
  quantity: string;
  description: string;
  connection: string;
  lastSynced: string;
  status: string;
  itemCode: string;
  productId: string;
  videoUrl: string | null;
  isCodAllowed: string | null;
  specialPrice: string | null;
  specialFromTime: string | null;
  specialToTime: string | null;
  listingUrl: string | null;
  warrantyType: string | null;
  salePrice: string | null;
  rejectReasons: string | null;
  available: string | null;
  shopSku: string | null;
}

const TIKTOK_FIELDS: ChannelFieldConfig = {
  title: "title",
  sku: "seller_sku",
  skuId: "sku_id",
  images: "main_images",
  category: "category_chains",
  brandName: "brand_name",
  price: "price",
  currency: "currency",
  weight: "package_weight",
  dimensions: "package_dimensions",
  dimensionWidth: null,
  dimensionHeight: null,
  dimensionLength: null,
  quantity: "inventory_quantity",
  description: "description",
  connection: "connection",
  lastSynced: "last_synced",
  status: "status",
  itemCode: "item_code",
  productId: "product_id",
  videoUrl: "video_url",
  isCodAllowed: "is_cod_allowed",
  salePrice: "sale_price",
  specialPrice: null,
  specialFromTime: null,
  specialToTime: null,
  listingUrl: null,
  warrantyType: null,
  rejectReasons: null,
  available: null,
  shopSku: null,
};

const LAZADA_FIELDS: ChannelFieldConfig = {
  title: "product_name",
  sku: "seller_sku",
  skuId: "item_id",
  images: "images",
  category: "primary_category",
  brandName: "brand",
  price: "price",
  currency: null,
  weight: "product_weight",
  dimensions: null,
  dimensionWidth: "package_width",
  dimensionHeight: "package_height",
  dimensionLength: "package_length",
  quantity: "quantity",
  description: "description",
  connection: "connection",
  lastSynced: "last_synced",
  status: "status",
  itemCode: "item_code",
  productId: "item_id",
  videoUrl: null,
  isCodAllowed: null,
  salePrice: null,
  specialPrice: "special_price",
  specialFromTime: "special_from_time",
  specialToTime: "special_to_time",
  listingUrl: "listing_url",
  warrantyType: "warranty_type",
  rejectReasons: "reject_reasons",
  available: "available",
  shopSku: "shop_sku",
};

const SHOPEE_FIELDS: ChannelFieldConfig = {
  title: "item_name",
  sku: "seller_sku",
  skuId: "shopee_item_id",
  images: "images",
  category: "category_name",
  brandName: "brand_name",
  price: "price",
  currency: "currency",
  weight: "weight",
  dimensions: null,
  dimensionWidth: "dimension_width",
  dimensionHeight: "dimension_height",
  dimensionLength: "dimension_length",
  quantity: "stock",
  description: "description",
  connection: "connection",
  lastSynced: "last_synced",
  status: "status",
  itemCode: "item_code",
  productId: "shopee_item_id",
  videoUrl: null,
  isCodAllowed: null,
  salePrice: null,
  specialPrice: null,
  specialFromTime: null,
  specialToTime: null,
  listingUrl: null,
  warrantyType: null,
  rejectReasons: null,
  available: null,
  shopSku: null,
};

/** Get the field config for a channel based on section.description */
export function getChannelConfig(channel: string): ChannelFieldConfig {
  if (channel === "Lazada") return LAZADA_FIELDS;
  if (channel === "Shopee") return SHOPEE_FIELDS;
  return TIKTOK_FIELDS; // Default to TikTok
}

/** Safe JSON parse — handles both strings and already-parsed objects */
export function parseJsonField<T = unknown>(value: unknown): T | null {
  if (value == null || value === "") return null;
  if (typeof value === "object") return value as T;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

/**
 * Extract image URLs from marketplace JSON — handles both formats:
 * - TikTok: [{ urls: ["https://..."] }, ...] (nested objects)
 * - Lazada: ["https://...", ...] (flat string array)
 */
export function extractImageUrls(value: unknown): string[] | null {
  const parsed = parseJsonField<unknown[]>(value);
  if (!parsed || !Array.isArray(parsed) || parsed.length === 0) return null;

  // Check if first element is a string → flat array (Lazada)
  if (typeof parsed[0] === "string") {
    const urls = parsed.filter((u): u is string => typeof u === "string" && u !== "");
    return urls.length > 0 ? urls : null;
  }

  // Otherwise assume nested structure (TikTok: { urls: [...] })
  const urls = parsed
    .flatMap((item: any) => {
      if (typeof item === "string") return [item];
      if (item?.urls && Array.isArray(item.urls)) return [item.urls[0]];
      if (item?.url && typeof item.url === "string") return [item.url];
      return [];
    })
    .filter((u): u is string => typeof u === "string" && u !== "");
  return urls.length > 0 ? urls : null;
}

/** Format TikTok category_chains JSON into breadcrumb text */
export function formatCategoryChain(value: unknown): string {
  const parsed = parseJsonField<Array<{ id?: string; name?: string; local_name?: string } | string>>(value);
  if (!parsed || !Array.isArray(parsed) || parsed.length === 0) return "\u2014";
  // Handle both [{name: "..."}, ...] and ["Cat1", "Cat2"] formats
  const names = parsed.map((item) =>
    typeof item === "string" ? item : (item?.name || item?.local_name || ""),
  ).filter(Boolean);
  return names.length > 0 ? names.join(" > ") : "\u2014";
}

/** Format dimensions from either TikTok JSON or Lazada separate fields */
export function formatDimensions(
  formData: Record<string, any>,
  config: ChannelFieldConfig,
): string | null {
  if (config.dimensions) {
    // TikTok: single JSON object
    const dims = parseJsonField<{ length?: number; width?: number; height?: number; unit?: string }>(
      formData[config.dimensions],
    );
    if (!dims) return null;
    const { length, width, height, unit } = dims;
    if (!length && !width && !height) return null;
    return `${length || 0} x ${width || 0} x ${height || 0} ${unit || "cm"}`;
  }
  // Lazada: separate fields
  const w = formData[config.dimensionWidth!];
  const h = formData[config.dimensionHeight!];
  const l = formData[config.dimensionLength!];
  if (!w && !h && !l) return null;
  return `${l || 0} x ${w || 0} x ${h || 0} cm`;
}

/** Standard iPad Settings divider */
export function Divider() {
  return <div className="mx-4 border-t border-border/40" />;
}

/** Standard iPad Settings key-value row */
export function SettingsRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center gap-4 px-4 py-5">
      <span
        className="text-lg w-1/2"
        style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}
      >
        {label}
      </span>
      <div className="text-lg w-1/2 text-right text-foreground">
        <span className="break-words whitespace-pre-wrap">{value || "\u2014"}</span>
      </div>
    </div>
  );
}
